import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { format, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';
import { CheckCircle2, Sparkles } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';
import { trackEvent } from '../utils/analytics';
import { buildPublicBookingUrl } from '../utils/booking';
import { applySeo } from '../utils/seo';

const STEPS = { SERVICE: 1, SLOT: 2, DETAILS: 3, CONFIRM: 4 };

function formatPrice(value) {
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function isPlaceholderPromoLabel(label) {
  const normalized = String(label || '').trim().toLowerCase();
  return normalized === 'humor' || normalized === 'test' || normalized === 'placeholder';
}

function getSafeServiceDescription(description) {
  const normalized = String(description || '').trim();

  if (!normalized) {
    return '';
  }

  if (normalized.toLowerCase().includes('masz wielkie usta')) {
    return 'Zabieg modelowania ust z naciskiem na naturalny efekt i komfort klientki.';
  }

  return normalized;
}

export default function BookingPage() {
  const { professionalKey } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.SERVICE);
  const [publicProfile, setPublicProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [expandedServiceIds, setExpandedServiceIds] = useState([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [slots, setSlots] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: '', countryCode: '+48', phone: '' });
  const [fieldErrors, setFieldErrors] = useState({ name: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmState, setConfirmState] = useState(null);
  const historyInitialized = useRef(false);
  const bookingReference = useMemo(() => {
    const normalizedKey = String(professionalKey || '').trim();
    const isLegacyId = /^\d+$/.test(normalizedKey);

    return {
      professionalId: isLegacyId ? Number.parseInt(normalizedKey, 10) : null,
      bookingSlug: isLegacyId ? '' : normalizedKey,
    };
  }, [professionalKey]);
  const publicServicesQuery = useMemo(() => {
    if (bookingReference.bookingSlug) {
      return `bookingSlug=${encodeURIComponent(bookingReference.bookingSlug)}`;
    }

    if (bookingReference.professionalId) {
      return `professionalId=${bookingReference.professionalId}`;
    }

    return '';
  }, [bookingReference.bookingSlug, bookingReference.professionalId]);

  const selectedAddons = (selectedService?.addons || []).filter((addon) => selectedAddonIds.includes(addon.id));
  const selectedServicePrice = Number(selectedService?.effective_price ?? selectedService?.promo_price ?? selectedService?.price ?? 0);
  const selectedServiceDuration = Number(selectedService?.duration_minutes || 0);
  const selectedAddonsPrice = selectedAddons.reduce((sum, addon) => sum + Number(addon.price || 0), 0);
  const selectedAddonsDuration = selectedAddons.reduce((sum, addon) => sum + Number(addon.duration_minutes || 0), 0);
  const hasUnsavedProgress = useMemo(() => (
    step < STEPS.CONFIRM && Boolean(
      selectedService ||
      selectedSlot ||
      selectedAddonIds.length > 0 ||
      form.name.trim() ||
      form.phone.trim()
    )
  ), [form.name, form.phone, selectedAddonIds.length, selectedService, selectedSlot, step]);

  useEffect(() => {
    if (!professionalKey || !publicServicesQuery) {
      setCatalogLoading(false);
      return;
    }

    setCatalogLoading(true);
    setError('');
    Promise.all([
      api.get(`/auth/public/${encodeURIComponent(professionalKey)}`),
      api.get(`/services?${publicServicesQuery}`),
    ])
      .then(([profileResponse, servicesResponse]) => {
        setPublicProfile(profileResponse.data);
        setServices(servicesResponse.data);
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setError('Ten link do rezerwacji jest nieaktywny albo został zmieniony.');
          return;
        }

        setError('Nie udało się załadować usług.');
      })
      .finally(() => setCatalogLoading(false));
  }, [professionalKey, publicServicesQuery]);

  useEffect(() => {
    if (!professionalKey) {
      return;
    }

    trackEvent('booking_entry', {
      booking_professional_key: professionalKey,
      booking_resolved_slug: bookingReference.bookingSlug || publicProfile?.booking_slug || '',
      booking_profile_id: publicProfile?.id || '',
    });
  }, [bookingReference.bookingSlug, professionalKey, publicProfile?.booking_slug, publicProfile?.id]);

  useEffect(() => {
    if (!professionalKey) {
      return;
    }

    const professionalName = publicProfile?.name ? `${publicProfile.name} - rezerwacja online` : 'Rezerwacja online | ClientWay';
    const description = publicProfile?.name
      ? `Umów wizytę online u ${publicProfile.name}. Wybierz usługę, termin i wyślij prośbę o rezerwację bez dzwonienia.`
      : 'Umów wizytę online, wybierz usługę i termin, a potem wyślij prośbę o rezerwację.';
    const bookingUrl = buildPublicBookingUrl(publicProfile?.booking_slug || professionalKey);

    applySeo({
      title: professionalName,
      description,
      keywords: 'rezerwacja online, kalendarz wizyt, umawianie wizyt, ClientWay booking',
      robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
      canonical: bookingUrl,
      ogTitle: professionalName,
      ogDescription: description,
      ogUrl: bookingUrl,
      themeColor: '#fffaf6',
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: professionalName,
        url: bookingUrl,
        description,
      },
    });
  }, [professionalKey, publicProfile?.booking_slug, publicProfile?.name]);

  useEffect(() => {
    if (!selectedService) return;
    setLoading(true);
    setSlots([]);
    api
      .get(`/availability/slots?${publicServicesQuery}&serviceId=${selectedService.id}&addonIds=${selectedAddonIds.join(',')}`)
      .then((r) => setSlots(r.data))
      .catch(() => setError('Nie udało się załadować terminów.'))
      .finally(() => setLoading(false));
  }, [publicServicesQuery, selectedAddonIds, selectedService]);

  useEffect(() => {
    if (!historyInitialized.current) {
      window.history.replaceState({ bookingStep: step }, '');
      historyInitialized.current = true;
      return;
    }
    window.history.pushState({ bookingStep: step }, '');
  }, [step]);

  useEffect(() => {
    function handlePopState(event) {
      const prevStep = event.state?.bookingStep;
      if (prevStep && Number.isInteger(prevStep)) {
        if (prevStep < step && hasUnsavedProgress) {
          window.history.pushState({ bookingStep: step }, '');
          setConfirmState({
            title: 'Opuścić bieżący krok?',
            description: 'Masz rozpoczętą rezerwację. Jeśli wrócisz teraz, część wpisanych danych może zostać utracona.',
            confirmLabel: 'Wróć mimo to',
            cancelLabel: 'Zostań tutaj',
            action: () => {
              setError('');
              setStep(prevStep);
            },
          });
          return;
        }
        setError('');
        setStep(prevStep);
      }
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasUnsavedProgress, step]);

  useEffect(() => {
    if (!hasUnsavedProgress) {
      return undefined;
    }

    function handleBeforeUnload(event) {
      event.preventDefault();
      event.returnValue = '';
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedProgress]);

  function handleServiceSelect(service) {
    setSelectedService(service);
    setSelectedAddonIds([]);
    setStep(STEPS.SLOT);
    setError('');
  }

  function validateName(nameValue) {
    if (!nameValue.trim()) {
      return 'Podaj imię.';
    }

    if (nameValue.trim().length < 2) {
      return 'Imię powinno mieć co najmniej 2 znaki.';
    }

    return '';
  }

  function validatePhone(phoneValue) {
    const digits = phoneValue.replace(/\D/g, '');
    if (digits.length < 9) {
      return 'Numer telefonu jest za krótki. Podaj 9 cyfr.';
    }
    if (digits.length > 9) {
      return 'Numer telefonu jest za długi. Podaj dokładnie 9 cyfr.';
    }
    return '';
  }

  function normalizePhone(countryCode, phoneValue) {
    const normalizedCountryCode = `+${String(countryCode || '+48').replace(/\D/g, '')}`;
    const digits = String(phoneValue || '').replace(/\D/g, '');
    return `${normalizedCountryCode}${digits}`;
  }

  function handleSlotSelect(slot) {
    setSelectedSlot(slot);
    setStep(STEPS.DETAILS);
    setError('');
  }

  function requestStepBack(targetStep) {
    if (!hasUnsavedProgress) {
      setError('');
      setStep(targetStep);
      return;
    }

    setConfirmState({
      title: 'Wrócić do poprzedniego kroku?',
      description: 'Jeśli wrócisz, aktualny wybór może wymagać ponownego ustawienia.',
      confirmLabel: 'Wróć',
      cancelLabel: 'Zostań',
      action: () => {
        setError('');
        setStep(targetStep);
      },
    });
  }

  function resetBookingFlow() {
    setSelectedService(null);
    setSelectedSlot(null);
    setSelectedAddonIds([]);
    setForm({ name: '', countryCode: '+48', phone: '' });
    setFieldErrors({ name: '', phone: '' });
    setError('');
    setStep(STEPS.SERVICE);
  }

  function handleCancelBooking() {
    if (!hasUnsavedProgress) {
      navigate('/', { replace: true });
      return;
    }

    setConfirmState({
      title: 'Anulować rezerwację?',
      description: 'Wyczyszczę wybraną usługę, termin i wpisane dane kontaktowe.',
      confirmLabel: 'Tak, wyczyść',
      cancelLabel: 'Wracam',
      action: resetBookingFlow,
    });
  }

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    setError('');
  }

  function handleFieldBlur(field) {
    if (field === 'name') {
      setFieldErrors((prev) => ({ ...prev, name: validateName(form.name) }));
    }

    if (field === 'phone') {
      setFieldErrors((prev) => ({ ...prev, phone: validatePhone(form.phone) }));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const nextErrors = {
      name: validateName(form.name),
      phone: !form.phone.trim() ? 'Podaj numer telefonu.' : validatePhone(form.phone),
    };

    setFieldErrors(nextErrors);

    if (nextErrors.name || nextErrors.phone) {
      setError('Popraw zaznaczone pola.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const normalizedPhone = normalizePhone(form.countryCode, form.phone);
      await api.post('/requests', {
        professional_id: publicProfile?.id,
        professional_slug: publicProfile?.booking_slug,
        service_id: selectedService.id,
        requested_slot_start: selectedSlot.start,
        client_name: form.name.trim(),
        client_phone: normalizedPhone,
        addon_ids: selectedAddonIds,
      });
      trackEvent('booking_request_sent', {
        booking_professional_key: professionalKey,
        booking_resolved_slug: publicProfile?.booking_slug || bookingReference.bookingSlug || '',
        service_id: selectedService.id,
        service_name: selectedService.name,
        selected_addon_count: selectedAddonIds.length,
        requested_slot_start: selectedSlot.start,
      });
      setStep(STEPS.CONFIRM);
    } catch (err) {
      const status = err.response?.status;
      const apiError = err.response?.data;
      const firstValidationError = apiError?.errors?.[0]?.msg;

      if (status === 429) {
        setError(
          apiError?.error ||
            'Chwileczkę, proszę. Wysłałaś prośbę przed momentem. Poczekaj kilka sekund i spróbuj ponownie.'
        );
      } else if (status === 409) {
        setError(
          apiError?.error ||
            'Już mamy Twoją prośbę na ten termin. Poczekaj na potwierdzenie lub wybierz inną godzinę.'
        );
      } else {
        setError(apiError?.error || firstValidationError || 'Coś poszło nie tak. Spróbuj ponownie.');
      }
    } finally {
      setLoading(false);
    }
  }

  function handleBookAnother() {
    setSelectedSlot(null);
    setForm({ name: '', countryCode: '+48', phone: '' });
    setFieldErrors({ name: '', phone: '' });
    setError('');
    setStep(STEPS.SERVICE);
  }

  function handlePickAnotherSlot() {
    setSelectedSlot(null);
    setFieldErrors({ name: '', phone: '' });
    setError('');
    setStep(STEPS.SLOT);
  }

  function groupByDate(slotList) {
    const groups = {};
    for (const slot of slotList) {
      const date = format(parseISO(slot.start), 'yyyy-MM-dd');
      if (!groups[date]) groups[date] = [];
      groups[date].push(slot);
    }
    return groups;
  }

  function toggleServiceDescription(serviceId) {
    setExpandedServiceIds((prev) => (
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    ));
  }

  return (
    <div className="booking-page">
      <header className="booking-header">
        <div className="booking-brand">
          <span className="booking-logo"><Sparkles size={16} strokeWidth={1.9} /></span>
          <div>
            <p className="booking-kicker">Rezerwacja online</p>
            <div className="booking-brand-name">{publicProfile?.name || 'ClientWay'}</div>
          </div>
        </div>
        {step < STEPS.CONFIRM && (
          <div className="booking-progress">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`progress-dot ${step >= s ? 'active' : ''}`} />
            ))}
          </div>
        )}
        <button type="button" className="btn-secondary booking-header-action" onClick={handleCancelBooking}>
          {hasUnsavedProgress ? 'Anuluj' : 'Zamknij'}
        </button>
      </header>

      <main className="booking-main">
        {error && <div className="alert alert-error">{error}</div>}

        {step === STEPS.SERVICE && (
          <div className="step">
            <h2 className="step-title">Wybierz usługę</h2>
            {catalogLoading && !error && <p className="loading-text">Ładowanie...</p>}
            {!catalogLoading && services.length === 0 && !error && (
              <p className="loading-text">Ta kosmetyczka nie ma jeszcze aktywnych usług do rezerwacji online.</p>
            )}
            <div className="service-list">
              {services.map((s) => (
                <div key={s.id} className="service-card service-card-shell">
                  <button className="service-card-main" onClick={() => handleServiceSelect(s)}>
                    <span className="service-card-copy">
                      <span className="service-name">{s.name}</span>
                      {s.promo_label && !isPlaceholderPromoLabel(s.promo_label) && <span className="service-promo-badge">{s.promo_label}</span>}
                    </span>
                    <span className="service-meta">
                      {s.duration_minutes} min · {formatPrice(s.effective_price ?? s.promo_price ?? s.price)} zł
                      {s.promo_price && Number(s.promo_price) < Number(s.price) ? <span className="service-meta-old">{s.price} zł</span> : null}
                    </span>
                  </button>
                  {getSafeServiceDescription(s.description) && (
                    <div className="service-card-details">
                      <span className={`service-description ${expandedServiceIds.includes(s.id) ? 'service-description--expanded' : 'service-description--collapsed'}`}>
                        {getSafeServiceDescription(s.description)}
                      </span>
                      {getSafeServiceDescription(s.description).length > 120 && (
                        <button
                          type="button"
                          className="service-description-toggle"
                          onClick={() => toggleServiceDescription(s.id)}
                        >
                          {expandedServiceIds.includes(s.id) ? 'Zwiń opis' : 'Rozwiń opis'}
                        </button>
                      )}
                    </div>
                  )}
                  {Array.isArray(s.addons) && s.addons.length > 0 && (
                    <span className="service-addon-preview">Dostępne dodatki od +{formatPrice(Math.min(...s.addons.map((addon) => Number(addon.price || 0))))} zł</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {step === STEPS.SLOT && (
          <div className="step">
            <div className="step-actions">
              <button type="button" className="back-btn" onClick={() => requestStepBack(STEPS.SERVICE)}>← Wróć</button>
              <button type="button" className="btn-ghost" onClick={handleCancelBooking}>Anuluj</button>
            </div>
            <h2 className="step-title">Wybierz termin</h2>
            <p className="step-subtitle">{selectedService.name} · {selectedServiceDuration + selectedAddonsDuration} min · {formatPrice(selectedServicePrice + selectedAddonsPrice)} zł</p>
            {selectedService.description && (
              <div className="booking-selection-summary booking-selection-summary--service">
                <span>{selectedService.description}</span>
              </div>
            )}
            {Array.isArray(selectedService.addons) && selectedService.addons.length > 0 && (
              <div className="booking-addon-box">
                <div className="section-title">Dodatki</div>
                <div className="addon-list">
                  {selectedService.addons.map((addon) => {
                    const active = selectedAddonIds.includes(addon.id);
                    return (
                      <button
                        key={addon.id}
                        type="button"
                        className={`addon-chip ${active ? 'addon-chip--selected' : ''}`}
                        onClick={() => {
                          setSelectedSlot(null);
                          setSelectedAddonIds((prev) => (
                            prev.includes(addon.id)
                              ? prev.filter((addonId) => addonId !== addon.id)
                              : [...prev, addon.id]
                          ));
                        }}
                      >
                        <span>{addon.name}</span>
                        <span>+{addon.price} zł · +{addon.duration_minutes} min</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {loading && <p className="loading-text">Szukam wolnych terminów...</p>}
            {!loading && slots.length === 0 && !error && (
              <p className="empty-text">Brak dostępnych terminów w najbliższych 30 dniach.</p>
            )}
            {!loading && slots.length > 0 && (
              <div className="slot-groups">
                {Object.entries(groupByDate(slots)).map(([date, daySlots]) => (
                  <div key={date} className="slot-day">
                    <h3 className="slot-day-label">
                      {format(parseISO(date), 'EEEE, d MMMM', { locale: pl })}
                    </h3>
                    <div className="slot-row">
                      {daySlots.map((slot) => (
                        <button key={slot.start} className="slot-btn" onClick={() => handleSlotSelect(slot)}>
                          {format(parseISO(slot.start), 'HH:mm')}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === STEPS.DETAILS && (
          <div className="step">
            <div className="step-actions">
              <button type="button" className="back-btn" onClick={() => requestStepBack(STEPS.SLOT)}>← Wróć</button>
              <button type="button" className="btn-ghost" onClick={handleCancelBooking}>Anuluj</button>
            </div>
            <h2 className="step-title">Twoje dane</h2>
            <p className="step-subtitle">
              {selectedService.name} · {format(parseISO(selectedSlot.start), "EEEE d MMMM", { locale: pl })}
            </p>
            <div className="booking-selection-summary">
              <span>Termin: {format(parseISO(selectedSlot.start), 'HH:mm')}</span>
              <span>Łącznie: {selectedServiceDuration + selectedAddonsDuration} min · {formatPrice(selectedServicePrice + selectedAddonsPrice)} zł</span>
            </div>
            <form onSubmit={handleSubmit} className="details-form">
              <div className="form-group">
                <label htmlFor="name">Imię</label>
                <input
                  id="name"
                  type="text"
                  placeholder="np. Kasia"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  onBlur={() => handleFieldBlur('name')}
                  className={fieldErrors.name ? 'input-error' : ''}
                  aria-invalid={fieldErrors.name ? 'true' : 'false'}
                  autoComplete="given-name"
                />
                {fieldErrors.name && <p className="field-error">{fieldErrors.name}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="phone">Numer telefonu</label>
                <div className="form-row booking-phone-row">
                  <select
                    value={form.countryCode}
                    onChange={(e) => updateField('countryCode', e.target.value)}
                    className="booking-country-code"
                    aria-label="Numer kierunkowy"
                  >
                    <option value="+48">+48</option>
                    <option value="+44">+44</option>
                    <option value="+49">+49</option>
                    <option value="+31">+31</option>
                    <option value="+33">+33</option>
                    <option value="+1">+1</option>
                  </select>
                  <input
                    id="phone"
                    type="tel"
                    placeholder="np. 600100200"
                    value={form.phone}
                    onChange={(e) => updateField('phone', e.target.value.replace(/\D/g, '').slice(0, 9))}
                    onBlur={() => handleFieldBlur('phone')}
                    className={fieldErrors.phone ? 'input-error' : ''}
                    aria-invalid={fieldErrors.phone ? 'true' : 'false'}
                    autoComplete="tel-national"
                    inputMode="numeric"
                    maxLength={9}
                  />
                </div>
                {fieldErrors.phone && <p className="field-error">{fieldErrors.phone}</p>}
                {!fieldErrors.phone && <p className="field-hint">Wyślemy potwierdzenie tylko na ten numer.</p>}
              </div>
              <p className="privacy-note">
                Podając dane, zgadzasz się na ich przetwarzanie w celu umówienia wizyty.
              </p>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Wysyłam...' : 'Wyślij prośbę o wizytę →'}
              </button>
            </form>
          </div>
        )}

        {step === STEPS.CONFIRM && (
          <div className="step confirm-step">
            <div className="confirm-icon confirm-icon--premium"><CheckCircle2 size={34} strokeWidth={1.8} /></div>
            <h2 className="step-title">Prośba wysłana!</h2>
            <p className="confirm-text">
              Cześć <strong>{form.name}</strong>! Twoja prośba o wizytę dotarła.
            </p>
            <div className="confirm-summary">
              <div className="confirm-row">
                <span>Usługa</span>
                <strong>{selectedService?.name}</strong>
              </div>
              {selectedAddons.length > 0 && (
                <div className="confirm-row">
                  <span>Dodatki</span>
                  <strong>{selectedAddons.map((addon) => addon.name).join(', ')}</strong>
                </div>
              )}
              <div className="confirm-row">
                <span>Łącznie</span>
                <strong>{selectedServiceDuration + selectedAddonsDuration} min · {formatPrice(selectedServicePrice + selectedAddonsPrice)} zł</strong>
              </div>
              <div className="confirm-row">
                <span>Termin</span>
                <strong>
                  {selectedSlot && format(parseISO(selectedSlot.start), "d MMMM HH:mm", { locale: pl })}
                </strong>
              </div>
            </div>
            <p className="confirm-wait">Jak tylko {publicProfile?.name || 'specjalistka'} potwierdzi termin, damy Ci znać.</p>
            <div className="confirm-actions">
              <button className="btn-primary" onClick={handlePickAnotherSlot}>
                Wybierz inny termin
              </button>
              <button className="btn-secondary" onClick={handleBookAnother}>
                Umów kolejną usługę
              </button>
            </div>
          </div>
        )}
      </main>

      <ConfirmDialog
        open={Boolean(confirmState)}
        title={confirmState?.title}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel}
        cancelLabel={confirmState?.cancelLabel}
        onCancel={() => setConfirmState(null)}
        onConfirm={() => {
          confirmState?.action?.();
          setConfirmState(null);
        }}
      />
    </div>
  );
}